import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const cornerBadgeStackSkill: Skill = {
    name: SKILL_NAMES.cornerBadgeStack,
    kind: 'codeo',
    description:
        'Stack short qualifying chips into a corner of the frame and clear them again — for ' +
        'certifications, included features, or the small print that supports a claim without ' +
        'competing with it.',
    instructions: `# Corner Badge Stack

Two or three short chips arrive in a corner, hold while the main claim is on screen, and leave. They carry the qualifications a headline should not have to — "SOC 2", "No card", "Cancel anytime" — so the headline can stay one line.

The register is deliberately secondary. If a badge is important enough to compete for attention, it is not a badge; it belongs in the headline.

## Markup

Anchor to the corner the main content leaves free, and keep the chips visually quieter than everything else in the frame.

\`\`\`html
<div id="badges" style="position: absolute; right: 120px; bottom: 120px; display: flex; flex-direction: column; align-items: flex-end; gap: 16px;">
  <div class="badge" style="padding: 14px 26px; border-radius: 999px; border: 2px solid var(--l5-color-1, #5b6472); font-family: var(--l5-font-0), sans-serif; font-size: 28px; font-weight: 600; letter-spacing: 0.02em; color: var(--l5-color-1, #5b6472);">SOC 2 Type II</div>
  <div class="badge" style="padding: 14px 26px; border-radius: 999px; border: 2px solid var(--l5-color-1, #5b6472); font-family: var(--l5-font-0), sans-serif; font-size: 28px; font-weight: 600; letter-spacing: 0.02em; color: var(--l5-color-1, #5b6472);">No card required</div>
  <div class="badge" style="padding: 14px 26px; border-radius: 999px; border: 2px solid var(--l5-color-1, #5b6472); font-family: var(--l5-font-0), sans-serif; font-size: 28px; font-weight: 600; letter-spacing: 0.02em; color: var(--l5-color-1, #5b6472);">Cancel anytime</div>
</div>
\`\`\`

## Timeline code

The chips arrive late — after the claim they qualify — and leave before the scene ends.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ARRIVE = 1.6;
const CLEAR = 5.2;

gsap.set('.badge', { x: 24, opacity: 0 });

tl.to('.badge', {
    x: 0,
    opacity: 1,
    duration: 0.42,
    ease: 'power3.out',
    stagger: 0.09,
}, ARRIVE);

tl.to('.badge', {
    x: 20,
    opacity: 0,
    duration: 0.32,
    ease: 'power2.in',
    stagger: { each: 0.06, from: 'end' },
}, CLEAR);

window.__timelines = { main: tl };
\`\`\`

The exit staggers from the end, so the stack unwinds the way it was built rather than dissolving as a block.

## Adapt

- **Horizontal strip**: a row along the bottom edge instead of a corner column, for four or more short chips. Same choreography.
- **Filled chips**: a solid low-contrast fill instead of an outline reads heavier and works better over busy backgrounds.
- **With icons**: a small mark inside each chip — see \`${SKILL_NAMES.svgIconEnrichment}\`. Only when the icons add meaning; three generic ticks are noise.
- **Sequenced with narration**: landing each chip on the phrase that names it makes the stack feel spoken rather than decorative.
- **Persist to the end**: dropping the clear is fine when the badges are the scene's closing qualification, but then something else must carry the motion.
- **One badge**: a single chip beside a headline is often stronger than three — see \`${SKILL_NAMES.lowerThirdCallout}\` when it needs to be an identification rather than a qualification.

## Pitfalls

- Chips are \`x\`-tweened, so none may carry a CSS \`transform\`.
- Anchor with \`right\`/\`bottom\` so the stack stays pinned as chip widths change.
- 28px is near the readable floor at 1920 wide. Do not go smaller to fit more in — cut a chip instead.
- Three is the maximum. A corner of five chips is small print, and small print in a video is not read by anyone.
- Badges must not arrive with the headline. Arriving together makes them compete; arriving after makes them support.
- Keep each chip to three words. A chip carrying a sentence is a caption in a rounded rectangle.
- These make factual claims — a compliance badge or a guarantee has to be one the video is entitled to state.`,
};
